// src/pages/FormSubjectComparePage.jsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
} from '@mui/material';
import { API_BASE } from '../config';
import { getFormById } from '../controllers/form.controller';
import { getFormResponses } from '../controllers/formResponse.controller';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { saveAs } from 'file-saver';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

import DatePicker from 'react-multi-date-picker';
import DateObject from 'react-date-object';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';

// ---- تنظیمات کش سمت کلاینت برای لیست فرم‌ها ----
const CLIENT_CACHE_TTL = 5 * 60 * 1000; // 5 دقیقه
const CLIENT_CACHE_MAX = 30;

// کمک‌کننده برای گرفتن id رشته‌ای از subject (ممکن است object populate شده یا string باشد)
const getSubjectKey = (subject) => {
  if (!subject) return null;
  if (typeof subject === 'string') return subject;
  if (subject._id) return subject._id.toString();
  try {
    return subject.toString();
  } catch {
    return null;
  }
};

// نام بیمار از روی subject populate شده
const buildSubjectName = (subject, indexForSameSubject = null) => {
  let base = 'بیمار';

  if (subject && typeof subject === 'object') {
    base =
      `${subject.firstName || ''} ${subject.lastName || ''}`.trim() ||
      subject.nationalId ||
      'بیمار';
  }

  if (indexForSameSubject != null) {
    return `${base} (#${indexForSameSubject})`;
  }

  return base;
};

// فرمت تاریخ شمسی
const formatFaDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return new Intl.DateTimeFormat('fa-IR').format(d);
};

const FormSubjectComparePage = () => {
  // فرم‌ها
  const [forms, setForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // فیلترها
  const [search, setSearch] = useState('');
  const [projectType, setProjectType] = useState(''); // trial / main / ''
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // paging فرم‌ها
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // کش فرم‌ها
  const formsCacheRef = useRef({}); // key -> { data, totalCount, ts }

  // فرم انتخاب‌شده برای مقایسه
  const [selectedFormId, setSelectedFormId] = useState('');
  const [formStructure, setFormStructure] = useState([]);

  // پاسخ‌های فرم انتخاب‌شده (برای جدول بیماران پایین)
  const [currentResponses, setCurrentResponses] = useState([]);
  const [loadingResponses, setLoadingResponses] = useState(false);

  // پاسخ‌های انتخاب‌شده برای مقایسه
  const [selectedResponseIds, setSelectedResponseIds] = useState([]);
  const [responsesByKey, setResponsesByKey] = useState({}); // key = responseId → کل پاسخ

  // --------- helpers عمومی ---------
  const buildCacheKey = (params) => JSON.stringify(params);
  const toEnglishDigits = (str = '') => str.replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d));


  // --------- دریافت لیست فرم‌ها با کش + فیلتر + paging ---------
  const loadForms = async () => {
    // const params = {
    //   page,
    //   limit,
    //   search: search || '',
    //   type: projectType || '',
    //   startDate: startDate ? startDate.toISOString() : '',
    //   endDate: endDate ? endDate.toISOString() : '',
    // };

    const params = {
      page,
      limit,
      search: search || '',
      type: projectType || '',
      startDate: startDate ? convertPersianToISO(startDate) : '',
      endDate: endDate ? convertPersianToISO(endDate) : '',
    };


    const cacheKey = buildCacheKey(params);
    const now = Date.now();

    // استفاده از کش اگر معتبر باشد
    const cached = formsCacheRef.current[cacheKey];
    if (cached && now - cached.ts < CLIENT_CACHE_TTL) {
      setForms(cached.data);
      setTotalCount(cached.totalCount);
      return;
    }

    setLoadingForms(true);
    try {
      const qs = new URLSearchParams(
        Object.entries(params).filter(([, v]) => v !== '' && v != null),
      ).toString();

      // ⚠️ اینجا آدرس درست: tree-for-access
      const res = await fetch(`${API_BASE}forms/tree-for-access?${qs}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });

      if (!res.ok) {
        console.error('خطا در دریافت فرم‌ها، کد:', res.status);
        setForms([]);
        setTotalCount(0);
        return;
      }

      const json = await res.json();
      const list = Array.isArray(json) ? json : json.data || [];

      setForms(list);
      setTotalCount(json.totalCount || 0);

      formsCacheRef.current[cacheKey] = {
        data: list,
        totalCount: json.totalCount || 0,
        ts: now,
      };

      // پاک‌سازی کش در صورت زیاد شدن
      const keys = Object.keys(formsCacheRef.current);
      if (keys.length > CLIENT_CACHE_MAX) {
        const oldestKey = keys.reduce((a, b) =>
          formsCacheRef.current[a].ts < formsCacheRef.current[b].ts ? a : b,
        );
        delete formsCacheRef.current[oldestKey];
      }
    } catch (err) {
      console.error('خطا در دریافت فرم‌ها', err);
      setForms([]);
      setTotalCount(0);
    } finally {
      setLoadingForms(false);
    }
  };

  // --------- دریافت ساختار فرم انتخاب‌شده ---------
  const loadFormStructure = async (formId) => {
    if (!formId) {
      setFormStructure([]);
      return;
    }
    try {
      const form = await getFormById(formId);
      setFormStructure(form.structure || []);
    } catch (err) {
      console.error('خطا در دریافت ساختار فرم', err);
      setFormStructure([]);
    }
  };

  // --------- دریافت پاسخ‌های یک فرم برای جدول بیماران ---------
  const loadFormResponses = async (formId) => {
    if (!formId) {
      setCurrentResponses([]);
      return;
    }
    setLoadingResponses(true);
    try {
      // از getFormResponses عمومی استفاده می‌کنیم (در بک‌اند: getAllFormResponses)
      const res = await getFormResponses({ formId, limit: 500 });
      const data = Array.isArray(res?.data) ? res.data : [];
      setCurrentResponses(data);

      // در صورت تغییر فرم، انتخاب‌ها را ریست کن
      setSelectedResponseIds([]);
      setResponsesByKey({});
    } catch (err) {
      console.error('خطا در دریافت پاسخ‌های فرم', err);
      setCurrentResponses([]);
    } finally {
      setLoadingResponses(false);
    }
  };

  // --------- useEffect ها ---------
  useEffect(() => {
    loadForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search, projectType, startDate, endDate]);

  useEffect(() => {
    // وقتی فرم عوض می‌شود، ساختار و پاسخ‌های آن را بگیر
    if (!selectedFormId) {
      setFormStructure([]);
      setCurrentResponses([]);
      setSelectedResponseIds([]);
      setResponsesByKey({});
      return;
    }
    loadFormStructure(selectedFormId);
    loadFormResponses(selectedFormId);
  }, [selectedFormId]);

  // --------- انتخاب پاسخ‌ها (چک‌باکس) ---------
  const toggleResponseSelection = (responseId) => {
    setSelectedResponseIds((prev) =>
      prev.includes(responseId) ? prev.filter((id) => id !== responseId) : [...prev, responseId],
    );
  };

  // --------- آماده‌سازی داده‌های مقایسه از روی پاسخ‌های انتخاب‌شده ---------
  const handleCompare = () => {
    console.log(selectedResponseIds);
    if (!selectedFormId || selectedResponseIds.length === 0) {
      console.log('Compared data empty,', selectedFormId, selectedResponseIds)
      return
    };

    const map = {};
    selectedResponseIds.forEach((rid) => {
      const resp = currentResponses.find((r) => r._id === rid);
      if (resp) {
        map[rid] = resp; // شامل answers, subjectId, submittedAt و ...
      }
    });
    console.log(map);
    setResponsesByKey(map);
  };

  // --------- محاسبه‌ها برای جدول و نمودار ---------
  const getCalculatedValueForKey = (field, key) => {
    const resp = responsesByKey[key];
    if (!resp || !Array.isArray(resp.answers)) return '—';
    const calc = field.calculation;
    if (!calc || !Array.isArray(calc.targetFields)) return '—';

    const nums = calc.targetFields.map((fid) => {
      const a = resp.answers.find((x) => x.fieldId === fid);
      return a ? parseFloat(a.value) || 0 : 0;
    });

    if (calc.operation === 'sum') return nums.reduce((s, n) => s + n, 0);
    if (calc.operation === 'avg') {
      return nums.length ? (nums.reduce((s, n) => s + n, 0) / nums.length).toFixed(2) : 0;
    }
    return '—';
  };

  const getAnswerValue = (key, field) => {
    const resp = responsesByKey[key];
    if (!resp || !Array.isArray(resp.answers)) return '—';

    if (field.type === 'calculated') return getCalculatedValueForKey(field, key);

    const ans = resp.answers.find((a) => a.fieldId === field.fieldId);
    if (!ans) return '—';

    const v = ans.value;
    if (typeof v === 'string' && v.startsWith('file-')) return '📎 فایل';
    if (Array.isArray(v)) return v.join('، ');
    if (typeof v === 'object' && v !== null) return JSON.stringify(v);
    return v || '—';
  };

  const getNumericSummary = (field) => {
    const values = selectedResponseIds.map((rid) => parseFloat(getAnswerValue(rid, field)) || 0);
    const valid = values.filter((v) => !isNaN(v));
    if (valid.length === 0) return null;

    const min = Math.min(...valid);
    const max = Math.max(...valid);
    const avg = (valid.reduce((a, b) => a + b, 0) / valid.length).toFixed(2);

    return { min, max, avg };
  };

  // فقط فیلدهای قابل مقایسه
  const comparableFields = formStructure.filter(
    (f) => f.compareEnabled || (f.compare && f.compare.enabled),
  );

  // داده‌ی نمودار برای هر فیلد
  const buildChartDataForField = (field) => {
    return selectedResponseIds.map((rid) => {
      const resp = responsesByKey[rid];
      if (!resp) return { name: 'نامشخص', value: 0 };

      const subjectKey = getSubjectKey(resp.subjectId);
      const sameSubjectResponses = currentResponses.filter(
        (r) => getSubjectKey(r.subjectId) && getSubjectKey(r.subjectId) === subjectKey,
      );
      const indexForSameSubject =
        sameSubjectResponses.findIndex((r) => r._id === resp._id) + 1 || null;

      const name = buildSubjectName(resp.subjectId, indexForSameSubject || null);
      const raw = getAnswerValue(rid, field);
      const value = parseFloat(raw);
      return {
        name,
        value: isNaN(value) ? 0 : value,
      };
    });
  };

  // نام نمایشی برای یک responseId
  const getDisplayNameForResponse = (rid) => {
    const resp = currentResponses.find((r) => r._id === rid);
    if (!resp) return 'بیمار';

    const subjectKey = getSubjectKey(resp.subjectId);
    const sameSubjectResponses = currentResponses.filter(
      (r) => getSubjectKey(r.subjectId) && getSubjectKey(r.subjectId) === subjectKey,
    );
    const indexForSameSubject =
      sameSubjectResponses.findIndex((r) => r._id === resp._id) + 1 || null;

    return buildSubjectName(resp.subjectId, indexForSameSubject || null);
  };

  // --------- خروجی Excel / PDF ---------
  const exportToExcel = () => {
    if (comparableFields.length === 0 || selectedResponseIds.length === 0) return;

    const header = ['سؤال / فیلد', ...selectedResponseIds.map((rid) => getDisplayNameForResponse(rid))];
    const data = comparableFields.map((field) => [
      field.titlep || 'بدون عنوان',
      ...selectedResponseIds.map((rid) => getAnswerValue(rid, field)),
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([header, ...data]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Compare');
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'compare-result.xlsx');
  };

  const exportToPDF = () => {
    if (comparableFields.length === 0 || selectedResponseIds.length === 0) return;

    const doc = new jsPDF('l', 'mm', 'a4');
    const header = ['سؤال / فیلد', ...selectedResponseIds.map((rid) => getDisplayNameForResponse(rid))];
    const rows = comparableFields.map((field) => [
      field.titlep || 'بدون عنوان',
      ...selectedResponseIds.map((rid) => getAnswerValue(rid, field)),
    ]);

    doc.text('مقایسه‌ی پاسخ‌های فرم بین بیماران', 14, 15);
    doc.autoTable({
      startY: 25,
      head: [header],
      body: rows,
      styles: { font: 'helvetica', fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [22, 160, 133] },
    });
    doc.save('compare-result.pdf');
  };

  const convertPersianToISO = (persianDate) => {
    if (!persianDate) return '';

    const d = new DateObject({
      date: persianDate,
      format: 'YYYY/MM/DD',
      calendar: persian,
    });

    return d.toDate().toISOString();
  };




  // --------- رندر ---------
  return (
    <Box sx={{ p: 3 }} dir="rtl">
      <Typography variant="h5" mb={2}>
        📊 مقایسه‌ی پاسخ‌های فرم بین بیماران
      </Typography>

      {/* فیلترها */}
      <Paper
        elevation={1}
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          backgroundColor: '#fafafa',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ mb: 1.5, fontWeight: 600 }}
        >
          فیلترها
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <TextField
            label="جستجو در عنوان / پروژه / کلینیک / صفحه"
            size="small"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
          />
          <TextField
            select
            label="نوع پروژه"
            size="small"
            value={projectType}
            onChange={(e) => {
              setPage(1);
              setProjectType(e.target.value);
            }}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">همه</MenuItem>
            <MenuItem value="trial">آزمایشی</MenuItem>
            <MenuItem value="main">اصلی</MenuItem>
          </TextField>

          <Box
            sx={{
              display: 'flex',
              gap: 4,                 // فاصله واقعی
              alignItems: 'flex-end',
            }}
          >
            {/* از تاریخ */}
            <Box sx={{ minWidth: 170 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5, textAlign: 'left', fontSize: 16, fontWeight: 500, color: 'text.secondary' }}
              >
                از تاریخ
              </Typography>

              <Box sx={{ width: 170, display: 'block' }}>
                <DatePicker
                  calendar={persian}
                  locale={persian_fa}
                  format="YYYY/MM/DD"
                  value={startDate ? new DateObject(startDate) : null}
                  onChange={(date) => {
                    setPage(1);
                    setStartDate(date ? toEnglishDigits(date.format('YYYY/MM/DD')) : null);
                  }}
                  style={{ width: '100%', height: 40 }}   // ✅ مهم
                />
              </Box>
            </Box>

            {/* تا تاریخ */}
            <Box sx={{ minWidth: 170 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1, textAlign: 'left', fontSize: 16, fontWeight: 500, color: 'text.secondary'}}
              >
                تا تاریخ
              </Typography>

              <Box sx={{ width: 170, display: 'block' }}>
                <DatePicker
                  calendar={persian}
                  locale={persian_fa}
                  format="YYYY/MM/DD"
                  value={endDate ? new DateObject(endDate) : null}
                  onChange={(date) => {
                    setPage(1);
                    setEndDate(date ? toEnglishDigits(date.format('YYYY/MM/DD')) : null);
                  }}
                  style={{ width: '100%', height: 40 }}   // ✅ مهم
                />
              </Box>
            </Box>
          </Box>


        </Box>
      </Paper>




      {/* جدول فرم‌ها */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Typography variant="subtitle1" mb={1}>
          لیست فرم‌ها
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>عنوان فرم</TableCell>
              <TableCell>پروژه</TableCell>
              <TableCell>کلینیک</TableCell>
              <TableCell>صفحه</TableCell>
              <TableCell>تاریخ ایجاد پروژه (شمسی)</TableCell>
              <TableCell align="center">عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingForms && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  در حال بارگذاری فرم‌ها...
                </TableCell>
              </TableRow>
            )}

            {!loadingForms && forms.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  فرمی برای نمایش وجود ندارد.
                </TableCell>
              </TableRow>
            )}

            {!loadingForms &&
              forms.map((f) => (
                <TableRow key={f.formId}>
                  <TableCell>{f.formTitle?.fa || f.formTitle?.en || 'بدون عنوان'}</TableCell>
                  <TableCell>{f.projectTitle?.fa || f.projectTitle?.en || '—'}</TableCell>
                  <TableCell>{f.clinicTitle?.fa || f.clinicTitle?.en || '—'}</TableCell>
                  <TableCell>{f.pageTitle?.fa || f.pageTitle?.en || '—'}</TableCell>
                  {/* ⚠️ اینجا از projectCreatedAt که توی pipeline ساختی استفاده می‌کنیم */}
                  <TableCell>{formatFaDate(f.projectCreatedAt)}</TableCell>
                  <TableCell align="center">
                    <Button
                      variant={selectedFormId === f.formId ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setSelectedFormId(f.formId)}
                    >
                      {selectedFormId === f.formId ? 'در حال مشاهده' : 'نمایش بیماران'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>

        {/* paging */}
        {totalCount > limit && (
          <Box
            sx={{
              mt: 1,
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <Button size="small" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
              صفحه قبل
            </Button>
            <Typography variant="body2">
              صفحه {page} از {Math.ceil(totalCount / limit) || 1}
            </Typography>
            <Button
              size="small"
              disabled={page >= Math.ceil(totalCount / limit)}
              onClick={() => setPage((p) => p + 1)}
            >
              صفحه بعد
            </Button>
          </Box>
        )}
      </Paper>

      {/* جدول بیماران و پاسخ‌ها برای فرم انتخاب‌شده */}
      {selectedFormId && (
        <Paper sx={{ mb: 3, p: 2 }}>
          <Typography variant="subtitle1" mb={1}>
            پاسخ‌های ثبت‌شده برای فرم انتخاب‌شده
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={40}></TableCell>
                <TableCell>بیمار</TableCell>
                <TableCell>تاریخ پاسخ (شمسی)</TableCell>
                <TableCell>شناسه پاسخ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingResponses && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    در حال بارگذاری پاسخ‌ها...
                  </TableCell>
                </TableRow>
              )}

              {!loadingResponses && currentResponses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    هنوز پاسخی برای این فرم ثبت نشده است.
                  </TableCell>
                </TableRow>
              )}

              {!loadingResponses &&
                currentResponses.map((resp) => {
                  const subjectKey = getSubjectKey(resp.subjectId);
                  const sameSubjectResponses = currentResponses.filter(
                    (r) =>
                      getSubjectKey(r.subjectId) &&
                      getSubjectKey(r.subjectId) === subjectKey,
                  );
                  const indexForSameSubject =
                    sameSubjectResponses.findIndex((r) => r._id === resp._id) + 1 || null;

                  return (
                    <TableRow key={resp._id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedResponseIds.includes(resp._id)}
                          onChange={() => toggleResponseSelection(resp._id)}
                        />
                      </TableCell>
                      <TableCell>
                        {buildSubjectName(resp.subjectId, indexForSameSubject || null)}
                      </TableCell>
                      {/* در مدل‌ات submittedAt داری، نه createdAt */}
                      <TableCell>{formatFaDate(resp.submittedAt)}</TableCell>
                      <TableCell>{resp._id}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              disabled={selectedResponseIds.length === 0}
              onClick={handleCompare}
            >
              مقایسه‌ی پاسخ‌های انتخاب‌شده
            </Button>
          </Box>
        </Paper>
      )
      }

      {/* جدول مقایسه */}
      {
        comparableFields.length > 0 &&
        selectedResponseIds.length > 0 &&
        Object.keys(responsesByKey).length > 0 && (
          <Paper sx={{ width: '100%', overflow: 'auto', p: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>سؤال / فیلد</TableCell>
                  {selectedResponseIds.map((rid) => (
                    <TableCell key={rid}>{getDisplayNameForResponse(rid)}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {comparableFields.map((field) => {
                  const summary =
                    field.compareDisplay === 'number-summary' ||
                      field.type === 'number' ||
                      field.type === 'calculated'
                      ? getNumericSummary(field)
                      : null;

                  return (
                    <React.Fragment key={field.fieldId}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          {field.titlep || 'بدون عنوان'}
                        </TableCell>
                        {selectedResponseIds.map((rid) => (
                          <TableCell key={rid}>{getAnswerValue(rid, field)}</TableCell>
                        ))}
                      </TableRow>

                      {summary && (
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell colSpan={selectedResponseIds.length + 1}>
                            <Typography
                              variant="body2"
                              sx={{ textAlign: 'left', color: '#555' }}
                            >
                              🔹 کمینه: {summary.min} | بیشینه: {summary.max} | میانگین:{' '}
                              {summary.avg}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>

            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="success"
                onClick={exportToExcel}
                disabled={comparableFields.length === 0}
              >
                خروجی Excel
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={exportToPDF}
                disabled={comparableFields.length === 0}
              >
                خروجی PDF
              </Button>
            </Box>
          </Paper>
        )
      }

      {/* نمودارها */}
      {
        comparableFields.length > 0 &&
        selectedResponseIds.length > 0 &&
        Object.keys(responsesByKey).length > 0 && (
          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {comparableFields.map((field) => {
              const display = field.compareDisplay;

              if (!display || display === 'table' || display === 'number-summary') return null;

              const data = buildChartDataForField(field);
              if (!data.length) return null;

              if (display === 'bar') {
                return (
                  <Box key={field.fieldId} sx={{ background: '#fff', p: 2, borderRadius: 2 }}>
                    <Typography mb={1} fontWeight="bold">
                      {field.titlep || 'نمودار'}
                    </Typography>
                    <BarChart width={400} height={250} data={data}>
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="value" name={field.titlep} />
                    </BarChart>
                  </Box>
                );
              }

              if (display === 'radar') {
                return (
                  <Box key={field.fieldId} sx={{ background: '#fff', p: 2, borderRadius: 2 }}>
                    <Typography mb={1} fontWeight="bold">
                      {field.titlep || 'نمودار راداری'}
                    </Typography>
                    <RadarChart
                      cx={200}
                      cy={150}
                      outerRadius={100}
                      width={400}
                      height={300}
                      data={data}
                    >
                      <PolarGrid />
                      <PolarAngleAxis dataKey="name" />
                      <PolarRadiusAxis />
                      <Radar
                        name={field.titlep}
                        dataKey="value"
                        stroke="#8884d8"
                        fill="#8884d8"
                        fillOpacity={0.6}
                      />
                      <Legend />
                    </RadarChart>
                  </Box>
                );
              }

              if (display === 'pie') {
                const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#a855f7'];
                return (
                  <Box key={field.fieldId} sx={{ background: '#fff', p: 2, borderRadius: 2 }}>
                    <Typography mb={1} fontWeight="bold">
                      {field.titlep || 'نمودار دایره‌ای'}
                    </Typography>
                    <PieChart width={400} height={250}>
                      <Pie
                        data={data}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label
                      >
                        {data.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </Box>
                );
              }

              return null;
            })}
          </Box>
        )
      }

      {
        comparableFields.length === 0 && (
          <Typography color="text.secondary" mt={2}>
            ابتدا فرم را انتخاب کرده و چند پاسخ را برای مقایسه تیک بزنید.
          </Typography>
        )
      }
    </Box >
  );
};

export default FormSubjectComparePage;
